import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, User, ShieldCheck, CalendarCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import JunagoLogo from "../components/JunagoLogo";

const BG_IMG = "https://images.unsplash.com/photo-1772960044680-38e08c76414d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjByb2FkJTIwdHJpcCUyMHZhY2F0aW9uJTIwY29hc3RhbHxlbnwwfHx8fDE3NzY0Njc4NTh8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "vendor") return <Navigate to="/vendor/pilotage" replace />;
  if (user) return <Navigate to="/mes-reservations" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, role: "client" });
      navigate("/mes-reservations");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel — lifestyle image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={BG_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/30" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <Link to="/"><JunagoLogo size={34} tone="light" /></Link>
          <div>
            <h1 className="font-heading text-4xl font-medium tracking-tight max-w-sm leading-tight" data-testid="register-panel-title">
              Réservez en toute simplicité.
            </h1>
            <p className="text-slate-200 mt-3 max-w-sm leading-relaxed text-sm">
              Créez votre compte et accédez à des centaines de véhicules disponibles près de chez vous.
            </p>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-300">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-violet-400" /> Paiement sécurisé</span>
              <span className="inline-flex items-center gap-1.5"><CalendarCheck className="h-4 w-4 text-violet-400" /> Confirmation instantanée</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — Form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="lg:hidden inline-block mb-10"><JunagoLogo size={34} tone="dark" /></Link>

          <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-900" data-testid="register-title">
            Créez votre compte
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Réservez en quelques clics et suivez vos réservations.
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Déjà inscrit ?{" "}
            <Link to="/login?context=client" className="text-violet-600 font-medium hover:underline">Connectez-vous</Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Nom complet</label>
              <div className="mt-1.5 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="Jean Dupont" data-testid="reg-name" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="jean@exemple.com" data-testid="reg-email" />
              </div>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Mot de passe</label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 focus:border-violet-600" placeholder="6 caractères minimum" data-testid="reg-password" />
              </div>
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="register-error">{error}</div>}

            <button type="submit" disabled={loading} className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full btn-primary font-semibold text-sm disabled:opacity-50" data-testid="register-submit">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Créer mon compte
            </button>
            <p className="text-xs text-slate-500 text-center">
              En continuant, vous acceptez les conditions d'utilisation de Junago.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
